<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Tasks' | term }}</h1>
                <v-spacer></v-spacer>
                <v-select
                    v-if="departments"
                    :items="departments"
                    item-value="id"
                    item-text="name"
                    v-model="selectedDepartment"
                    :label="$t('tasks.department')"
                    single-line
                    hide-details
                    class="admin-table-filter"
                    :menu-props="{ dark: true }"
                >
                    <template v-slot:item="data">
                        <template v-if="data.item.isGroup">
                            <v-list-item-content
                                class="caption"
                                @click="$event.stopPropagation()"
                                v-text="data.item.name"
                            ></v-list-item-content>
                        </template>
                        <template v-else>
                            <v-list-item-content class="pl-5" v-text="data.item.name"></v-list-item-content>
                        </template>
                    </template>
                </v-select>
            </v-card-title>
            <empty-state v-if="!selectedDepartment" icon="mdi-alert-circle-outline" :message="$t('shared.message.select-a', { term: $termSync('Department') })"></empty-state>

            <v-card-title v-if="!!selectedDepartment">
                <h2 class="subtitle-1">{{ $t('tasks.system-term', { term: $termSync('Tasks') }) }}</h2>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers="headers"
                :items="filteredSystem"
                v-if="!!selectedDepartment"
            >
                <template v-slot:item.defaultTimeMinutes="{ item }">
                    <span v-if="item.quantityMetric != null" class="font-italic">By {{ quantityMetricNames[item.quantityMetric]
                        }}</span>
                    <span v-else>{{ item.defaultTimeMinutes }}</span>
                </template>
                <template v-slot:item.x="{ item }">
                    <div class="my-1" style="max-width: 500px;">
                        <week-time-period-display
                            :name="item.name"
                            :colour="item.backgroundColour"
                            :text-colour="item.foregroundColour"
                            :is-delay="item.isDelay"
                            :hide-name-if-delay="false"
                        >
                        </week-time-period-display>
                    </div>
                </template>
                <template v-slot:item.id="{ item }">
                    <v-btn text icon @click="edit(item)" class="float-right">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                </template>
            </v-data-table>

            <v-card-title v-if="!!selectedDepartment">
                <h2 class="subtitle-1">{{ $t('tasks.custom-term', { term: $termSync('Tasks') }) }}</h2>
                <v-spacer></v-spacer>
                <v-btn small color="secondary" class="ml-3" @click="create()" v-if="!!selectedDepartment">
                    <v-icon left>mdi-plus</v-icon>{{ $t('shared.button.add') }}
                </v-btn>
                <v-menu
                    v-model="importDialog"
                    max-width="310px"
                    :close-on-content-click="false"
                    v-if="!!selectedDepartment"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" small class="ml-1" color="secondary">
                            <v-icon left>mdi-upload</v-icon>{{ $t('shared.button.import') }}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-text>
                            <input ref="inputUpload" type="file" accept="text/csv" @change="getFile($event)" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" @click="importData">{{ $t('shared.button.upload') }}</v-btn>
                            <v-btn text @click="importDialog = false">{{ $t('shared.button.cancel') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn
                    small
                    class="ml-1"
                    color="gray"
                    v-if="!!selectedDepartment && filteredActiveUserDefined.length"
                    @click="exportData()"
                >
                    <v-icon left>mdi-download</v-icon>{{ $t('shared.button.export') }}
                </v-btn>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers="headers"
                :items="filteredActiveUserDefined"
                v-if="!!selectedDepartment"
            >
                <template v-slot:item.defaultTimeMinutes="{ item }">
                    <span v-if="item.quantityMetric != null" class="font-italic">By {{ quantityMetricNames[item.quantityMetric]
                        }}</span>
                    <span v-else>{{ item.defaultTimeMinutes }}</span>
                </template>
                <template v-slot:item.x="{ item }">
                    <div class="my-1" style="max-width: 500px;">
                        <week-time-period-display
                            :name="item.name"
                            :colour="item.backgroundColour"
                            :text-colour="item.foregroundColour"
                            :is-delay="item.isDelay"
                            :hide-name-if-delay="false"
                        >
                        </week-time-period-display>
                    </div>
                </template>
                <template v-slot:item.id="{ item }">
                    <v-btn text icon @click="edit(item)" class="float-right">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
            <v-card-title v-if="filteredArchivedUserDefined.length > 0">
                <h2 class='subtitle-1'><v-icon color="primary" left>mdi-archive-outline</v-icon>{{ $t('tasks.archived-term', { term: $pluralSync('Tasks') }) }}</h2>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers="headers"
                :items="filteredArchivedUserDefined"
                v-if="filteredArchivedUserDefined.length > 0"
            >
                <template v-slot:item.defaultTimeMinutes="{ item }">
                    <span v-if="item.quantityMetric != null" class="font-italic">By {{ quantityMetricNames[item.quantityMetric]
                        }}</span>
                    <span v-else>{{ item.defaultTimeMinutes }}</span>
                </template>
                <template v-slot:item.x="{ item }">
                    <div class="my-1" style="max-width: 500px;">
                        <week-time-period-display
                            :name="item.name"
                            :colour="item.backgroundColour"
                            :text-colour="item.foregroundColour"
                            :is-delay="item.isDelay"
                            :hide-name-if-delay="false"
                        >
                        </week-time-period-display>
                    </div>
                </template>
                <template v-slot:item.id="{ item }">
                    <v-btn text icon @click="edit(item)" class="float-right">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>
        <standard-dialog :render="!!selected" :value='showModal' :max-width="900" persistent>
            <template v-slot:title>
                <span v-if="selected.id">{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class="ml-1">{{ 'Task' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="form" :lazy-validation="true">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-alert type="info" class="white--text" style="font-size:0.9em">
                                    <strong>{{ $t('tasks.naming-tasks') }}</strong> {{ $t('tasks.future-tense-and-in-progress-are-the-names-used-to-refer-to-this-task-in-the-future-or-in-progress-e-g-for-bog-the-future-tense-would-be-to-bog-and-in-progress-would-be-bogging') }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="9" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('shared.label.name')"
                                    :rules="[$rules.required(), $rules.maxLength(50)]"
                                    v-model="selected.name"
                                    hint="e.g. Bolt & Mesh"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="3" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('tasks.abbreviation')"
                                    :rules='[$rules.notWhitespace(), $rules.maxLength(20)]'
                                    v-model="selected.abbrName"
                                    hint="e.g. B & M"
                                    counter="20"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('tasks.name-future-tense')"
                                    :disabled="selected.isDelay"
                                    :rules="[$rules.requiredIf(() => !selected.isDelay), $rules.maxLength(50)]"
                                    v-model="selected.nameToDo"
                                    hint="e.g. To Bolt & Mesh"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('tasks.name-when-in-progress')"
                                    :disabled="selected.isDelay"
                                    :rules="[$rules.requiredIf(() => !selected.isDelay), $rules.maxLength(50)]"
                                    v-model="selected.nameInProgress"
                                    hint="e.g. Bolting & Meshing"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-select
                                    autocomplete="off"
                                    :label="$t('tasks.category-type')"
                                    v-model="selected.taskCategoryId"
                                    type="number"
                                    item-value="id"
                                    item-text="name"
                                    :items="filteredTaskCategories"
                                    :menu-props="{ dark: true }"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row v-if="isAdmin">
                            <v-col cols="12" class="pa-2">
                                <v-alert type="warning" class="white--text" style="font-size:0.9em">
                                    <strong>{{ $t('tasks.system-task-class') }}</strong> {{ $t('tasks.this-a-system-setting-and-changing-this-will-have-system-wide-effects-changing-task-class-can-change-system-behaviour-this-is-an-administration-action-only') }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row v-if="isAdmin">
                            <v-col cols="12" class="pa-2">
                                <v-select
                                    autocomplete="off"
                                    :label="$t('tasks.system-task-class')"
                                    v-model="selected.taskClass"
                                    item-value="value"
                                    item-text="name"
                                    :items="availableTaskClasses"
                                    :menu-props="{ dark: true }"
                                    clearable
                                >
                                    <template v-slot:append-outer>
                                        <v-tooltip v-if="taskClassesRemoved" bottom>
                                            <template v-slot:activator="{ on }">
                                                <v-icon v-on="on" class="ml-1">mdi-information-outline</v-icon>
                                            </template>
                                            <span>{{ $t('tasks.some-task-classes-removed') }}</span>
                                        </v-tooltip>
                                    </template>
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-checkbox
                                    :label="$t('tasks.is-delay')"
                                    v-model='selected.isDelay'
                                    @change="onChangeTaskIsDelay"
                                    hide-details></v-checkbox>
                            </v-col>
                            <v-col cols="6">
                                <v-checkbox
                                    :label="$t('tasks.allocate-actuals-to-next-cut')"
                                    v-model='selected.allocateToNextCut'
                                    hide-details></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="4" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('tasks.default-time-mins')"
                                    :rules="[$rules.required(), $rules.multipleOf(15, '15 minute increments only')]"
                                    v-model="selected.defaultTimeMinutes"
                                    type="number"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="4" class="pa-2" v-if="selected.isDelay">
                                <v-select
                                    autocomplete="off"
                                    :label="$t('tasks.delay-type')"
                                    v-model="selected.delayType"
                                    :rules="[$rules.required()]"
                                    type="number"
                                    :items="['Float', 'Fixed']"
                                    :menu-props="{ dark: true }"
                                ></v-select>
                            </v-col>
                            <v-col cols="4" class="pa-2" v-if="!selected.isDelay">
                                <v-select
                                    autocomplete="off"
                                    :label="$t('tasks.metric')"
                                    clearable
                                    v-model="selected.quantityMetric"
                                    :items="quantityMetrics"
                                    :rules="[]"
                                    :menu-props="{ dark: true }"
                                    :disabled="!permitUpdatingQuantityMetric(selected)"
                                ></v-select>
                            </v-col>
                            <v-col cols="4" class="pa-2" v-if="!selected.isDelay && showLinkedActuals">
                                <v-autocomplete
                                    autocomplete="off"
                                    :label="$t('tasks.linked-physical')"
                                    clearable
                                    v-model="selected.linkedActualsDefinitionId"
                                    :items="numericActualsDefinitions"
                                    item-value="id"
                                    item-text="definitionName"
                                    :rules="[]"
                                    :menu-props="{ dark: true }"
                                    :disabled="!permitUpdatingQuantityMetric(selected)"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="5">
                                <h2 class="pa-2 title">{{ $t('tasks.task-colour') }}</h2>
                                <v-color-picker
                                    class="ma-2"
                                    v-model="selected.backgroundColour"
                                    :swatches="swatch"
                                    show-swatches
                                ></v-color-picker>
                            </v-col>
                            <v-col cols="5" v-if="!selected.isDelay">
                                <h2 class="pa-2 title">{{ $t('tasks.text-colour') }}</h2>
                                <v-color-picker
                                    class="ma-2"
                                    v-model="selected.foregroundColour"
                                    :swatches="textSwatches"
                                    show-swatches
                                ></v-color-picker>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="2">{{ $t('tasks.sample') }}</v-col>
                            <v-col cols="6">
                                <div style="max-width: 500px;">
                                    <week-time-period-display
                                        :name="selected.name"
                                        :colour="selected.backgroundColour"
                                        :text-colour="selected.foregroundColour"
                                        :is-delay="selected.isDelay"
                                        :hide-name-if-delay="false"
                                    >
                                    </week-time-period-display>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6" class="pa-2">
                                <v-select
                                    autocomplete="off"
                                    :label="$t('tasks.default-split-delay-type')"
                                    v-model="selected.defaultSplitDelayTaskTypeId"
                                    :items="availableDefaultDelays"
                                    item-value="id"
                                    item-text="name"
                                    hide-details
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
                <v-alert
                    v-if='editingSelected && !selected.canDelete && !selected.loadingCanDelete && !selected.isArchived'
                    color='warning'
                    class="white--text">
                    This {{ 'Task' | term }} has been used to plan work, used in a cycle, or as a default delay, and cannot be deleted.
                </v-alert>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='editingSelected && !selected.isArchived' @click='del' :disabled='!selected.canDelete' :loading='selected.loadingCanDelete'>{{ $t('shared.button.delete') }}</v-btn>
                <v-btn color="red" text v-if="editingSelected && !selected.isArchived && selected.taskClass === TaskClasses.UserDefined" @click="archiveTaskType"><v-icon left>mdi-archive-outline</v-icon>{{ $t('shared.button.archive') }}</v-btn>
                <v-btn color="red" text v-if="editingSelected && selected.isArchived" @click="restoreTaskType"><v-icon left>mdi-archive-arrow-up-outline</v-icon>{{ $t('shared.button.restore') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save()' v-if="!selected.isArchived">{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import MineAreas from '@/lib/data/MineAreas';
import TaskTypes from '@/lib/data/TaskTypes';
import Palettes from '@/lib/Palettes';
import TaskCategories from '@/lib/data/TaskCategories';
import AppHttp from '../../lib/AppHttp';
import DownloadHelper from '@/lib/DownloadHelper';
import { QuantityMetric, TaskClasses } from '@/models/api';
import Security, { Roles } from '@/lib/Security';
import { IsFloatDelayType } from '@/lib/services/TaskType';
import WeekTimePeriodDisplay from '@/components/Board/TimePeriodDisplay.vue';
import ShiftTaskContents from '@/components/ShiftBoard/ShiftTaskContents.vue';
import Actuals from '@/lib/data/Actuals';
import { IsNumericActualsDefinition } from '@/components/Admin/Shared/ActualsDefinitionUtils';
import Config from '@/lib/Config';

export default {
    components: { WeekTimePeriodDisplay },
    data() {
        return {
            data: null,
            departments: null,
            taskCategories: null,
            selectedDepartment: null,
            showModal: false,
            selected: null,
            editingSelected: false,
            importDialog: false,
            file: null,
            textSwatches: [['#000000'], ['#444444'], ['#999999'], ['#ffffff'], ['#ff3333']],
            headers: [
                { text: 'Name', value: 'name', width: '35%' },
                { text: this.$t('tasks.default-time-mins-col'), value: 'defaultTimeMinutes', width: '15%' },
                { text: 'Sample', value: 'x', width: '35%' },
                { text: '', value: 'id', width: '15%' }
            ],
            quantityMetrics: [
                { value: QuantityMetric.Distance, text: 'Distance' },
                { value: QuantityMetric.WeightTonnes, text: this.$t('tasks.weight-t') },
                { value: QuantityMetric.WeightKilograms, text: this.$t('tasks.weight-kg')},
                { value: QuantityMetric.Count, text: 'Count' },
            ],
            quantityMetricNames: quantityMetricNames,
            actualsDefinitions: [],
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            var mineAreas = await MineAreas.get(['Departments', 'Departments.Palettes']);
            this.data = await TaskTypes.get();
            this.taskCategories = await TaskCategories.get([]);

            var departments = [];
            for (let mine of mineAreas) {
                departments.push({ id: mine.id, name: mine.name, isGroup: true });
                for (let dept of mine.departments) {
                    departments.push({
                        id: dept.id,
                        mineAreaId: mine.id,
                        name: dept.name,
                        isGroup: false,
                        palette: Palettes.makePalette(dept.palettes)
                    });
                }
            }
            this.departments = departments;

            if (this.selectedDepartment) {
                await this.loadActualsDefinitions();
            }
        },
        async loadActualsDefinitions() {
            if (!this.selectedDepartment) {
                this.actualsDefinitions = [];
                return;
            }

            try {
                const definitions = await Actuals.getDefinitions(this.selectedDepartment);
                this.actualsDefinitions = definitions;
            } catch (error) {
                console.error(this.$t('tasks.error-loading-actuals-definitions'), error);
                this.actualsDefinitions = [];
            }
        },
        create() {
            this.edit({
                departmentId: this.selectedDepartment,
                backgroundColour: '#89c34a',
                foregroundColour: '#333333',
                taskClass: TaskClasses.UserDefined,
            }, true);
        },
        edit(item, creating = false) {
            this.selected = {
                ...item,
                loadingCanDelete: !creating,
                linkedActualsDefinitionId: item.linkedActualsDefinitionId || null
            };

            if(!creating){
                TaskTypes.canDelete(this.selected.id).then(response => {
                    if(this.selected?.id === item.id)
                        this.selected.canDelete = response.canDelete;
                }).finally(() => {
                    if(this.selected?.id === item.id)
                        this.selected.loadingCanDelete = false;
                });
            }

            this.editingSelected = !creating;
            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if(this.selected.id){
                await this.updateTaskType(this.selected);
            } else {
                await this.createTaskType(this.selected);
            }

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Task')} Saved`);
            this.showModal = false;
            await this.loadData();
        },
        async del() {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Task'),  { })) {
                await TaskTypes.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Task')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        async archiveTaskType() {
            if(this.selected == null)
                return;

            this.$wait.start('saving');
            try {
                await TaskTypes.archiveTaskType(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Task')} Archived`);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async restoreTaskType() {
            if(this.selected == null)
                return;

            this.$wait.start('saving');
            try {
                await TaskTypes.restoreTaskTypeFromArchive(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Task')} Restored`);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async exportData() {
            const result = await TaskTypes.export(this.selectedDepartment);
            var csv = result.csvFile;
            var blob = DownloadHelper.makeBlobFromFileString(csv);
            var selectedDept = this.departments.filter(x => x.id == this.selectedDepartment)[0];
            var deptName = selectedDept.name.replace(/[/\\?%*:|"<>]/g, '');
            DownloadHelper.download(`Tasks-${deptName}.csv`, blob);
        },
        async importData() {
            try {
                this.$wait.start('saving');
                this.uploadResult = null;
                await TaskTypes.import(this.selectedDepartment, this.file);
                this.importDialog = false;
                EventBus.$emit(Events.ToastSuccess, this.$t('tasks.term-imported', { term: this.$termSync('Tasks') }));
                await this.loadData();
            } finally {
                this.$wait.end('saving');
            }
        },

        async getFile(e) {
            this.file = e.target.files[0];
        },
        permitUpdatingQuantityMetric(taskType) {
            return !this.isSystemTask(taskType) || taskType.taskClass === TaskClasses.Charge;
        },
        isSystemTask(taskType) {
            return taskType && taskType.taskClass !== TaskClasses.UserDefined;
        },

        canDelete(taskType) {
            return taskType && taskType.id && !this.isSystemTask(taskType);
        },
        onChangeTaskIsDelay() {
            this.selected.delayType = null;
            this.selected.quantityMetric = null;
            this.selected.linkedActualsDefinitionId = null;
        },
        async updateTaskType(taskType){
            return await TaskTypes.update({
                _type: 'UpdateTaskTypeCommand',
                id: taskType.id,
                name: taskType.name,
                nameToDo: taskType.nameToDo,
                nameInProgress: taskType.nameInProgress,
                abbrName: taskType.abbrName,
                backgroundColour: taskType.backgroundColour,
                foregroundColour: taskType.foregroundColour,
                defaultTimeMinutes: taskType.defaultTimeMinutes,
                isDelay: taskType.isDelay,
                defaultSplitDelayTaskTypeId: taskType.defaultSplitDelayTaskTypeId,
                taskCategoryId: taskType.taskCategoryId,
                delayType: taskType.delayType,
                taskClass: taskType.taskClass,
                quantityMetric: taskType.quantityMetric,
                linkedActualsDefinitionId: taskType.linkedActualsDefinitionId,
                allocateToNextCut: taskType.allocateToNextCut
            });
        },
        async createTaskType(taskType){
            return await TaskTypes.create({
                _type: 'CreateTaskTypeCommand',
                departmentId: taskType.departmentId,
                name: taskType.name,
                nameToDo: taskType.nameToDo,
                nameInProgress: taskType.nameInProgress,
                abbrName: taskType.abbrName,
                backgroundColour: taskType.backgroundColour,
                foregroundColour: taskType.foregroundColour,
                defaultTimeMinutes: taskType.defaultTimeMinutes,
                isDelay: taskType.isDelay,
                defaultSplitDelayTaskTypeId: taskType.defaultSplitDelayTaskTypeId,
                taskCategoryId: taskType.taskCategoryId,
                delayType: taskType.delayType,
                taskClass: taskType.taskClass,
                quantityMetric: taskType.quantityMetric,
                linkedActualsDefinitionId: taskType.linkedActualsDefinitionId,
                allocateToNextCut: taskType.allocateToNextCut
            });
        }
    },
    computed: {
        TaskClasses() {
            return TaskClasses
        },
        hasConventionalBoggingTask() {
            return this.data && this.data.some(x => x.taskClass === TaskClasses.ConventionalBogging);
        },
        hasRemoteBoggingTask() {
            return this.data && this.data.some(x => x.taskClass === TaskClasses.RemoteBogging);
        },
        filteredSystem() {
            if (!this.data || !this.selectedDepartment) {
                return [];
            }
            return this.data.filter(x => x.departmentId == this.selectedDepartment && x.taskClass !== TaskClasses.UserDefined);
        },
        filteredUserDefined() {
            if (!this.data || !this.selectedDepartment) {
                return [];
            }
            return this.data.filter(x => x.departmentId == this.selectedDepartment && x.taskClass === TaskClasses.UserDefined);
        },
        filteredActiveUserDefined() {
            return this.filteredUserDefined.filter(x => !x.isArchived);
        },
        filteredArchivedUserDefined() {
            return this.filteredUserDefined.filter(x => x.isArchived);
        },
        filteredTaskCategories() {
            if (!this.taskCategories || !this.selectedDepartment) {
                return [];
            }

            var dept = this.departments.filter(x => x.id == this.selectedDepartment)[0];
            return this.taskCategories.filter(x => x.mineAreaId == dept.mineAreaId);
        },
        availableTaskClasses() {
            return this.taskClasses.filter(x=> (!this.hasConventionalBoggingTask || x.value !== TaskClasses.ConventionalBogging) && (!this.hasRemoteBoggingTask || x.value !== TaskClasses.RemoteBogging));
        },
        taskClasses() {
            const classes = Object.entries(TaskClasses)
                .filter(([key, value]) => !isNaN(value))
                .map(([key, value]) => ({name: key, value}));
            return classes;
        },
        taskClassesRemoved() {
            return this.availableTaskClasses.length < this.taskClasses.length;
        },
        delays() {
            if (!this.data || !this.selectedDepartment) {
                return [];
            }
            return this.data.filter(x => x.departmentId == this.selectedDepartment && x.isDelay);
        },
        floatDelays() {
            return this.delays.filter(x=>IsFloatDelayType(x));
        },
        availableDefaultDelays() {
            if(this.selected == null)
                return [];

            return this.floatDelays.filter(x=>!x.isArchived || x.id === this.selected.defaultSplitDelayTaskTypeId);
        },
        showLinkedActuals() {
            return Config?.FeatureFlags?.EnableTaskLinkedActuals ?? false;
        },
        numericActualsDefinitions() {
            return this.actualsDefinitions.filter(def => IsNumericActualsDefinition(def.definitionType));
        },
        swatch() {
            var dept = this.departments.filter(x => x.id == this.selectedDepartment)[0];
            return dept ? dept.palette : [];
        },
        isAdmin() {
            return Security.hasRole(Roles.Administrator)
        }
    },
    watch: {
        async selectedDepartment(newValue) {
            if (newValue) {
                await this.loadActualsDefinitions();
            }
        }
    }
};

const quantityMetricNames = {
    [QuantityMetric.Distance]: 'distance',
    [QuantityMetric.WeightTonnes]: 'weight (t)',
    [QuantityMetric.WeightKilograms]: 'weight (kg)',
    [QuantityMetric.Count]: 'count',
    [QuantityMetric.LocationSpecific]: 'location specific measure'
}
</script>
